import { Box, Button, Stack, styled, Typography } from "~/common/mui";
import { useDispatch, useSelector } from "react-redux";
import {
  getCurrentStep,
  getHelpUrl,
  getIsInConflict,
  getShowLoader,
  getSteps,
  selectIsConfiguratorAddedToCartAlready,
  selectIsConfiguratorAddToCartEnabled,
  selectIsSaveConfigurationEnabled,
} from "~/features/configurator/Linear/redux/selectors";
import { userIsCustomer, userIsPublic, userIsRegistered } from "~/domains/user/utils/roles";
import { PageTitle } from "~/common/components/PageTitle/v2/PageTitle";
import { KbComponent } from "~/features/configurator/common/components/KbComponent";
import { LinearTeaser } from "~/features/configurator/Linear/components/LinearTeaser";
import { LinearStepNavigation } from "~/features/configurator/common/components/Navigation/LinearNavigation";
import Step from "~/features/configurator/Linear/components/Step";
import FullscreenLoader from "~/features/loading/FullscreenLoader";
import InConflictModal from "~/features/configurator/Linear/components/InConflictModal";
import { type ConfiguratorPageDefinition } from "~/features/configurator/ConfiguratorPageDefinition";
import { useXmlConfiguratorTranslations } from "~/features/configurator/common/useXmlConfiguratorTranslations";
import { ProductService } from "~/features/addToCart/api/ProductService";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";
import ResetLink from "~/features/configurator/common/components/Navigation/ResetLink";
import { Link } from "~/common/components/Link";
import { getRedirectToRegisterPage, redirectOrReload, redirectToUrl } from "~/utils/url/urlUtils";
import { useSaveConfigPopup } from "~/features/configurator/Linear/hooks/useSaveConfigPopup";
import { CART_PAGE, REGISTER_PAGE, SAVED_CONFIGS_PAGE } from "~/utils/url/staticUrls";
import { useTrackLCSimpleButtonClick } from "~/features/configurator/Linear/hooks/useTrackLCClicks";
import Card, { CardSize } from "~/common/components/Card";
import { IconType } from "~/common/components/icon/IconType";
import { DefaultButtonIcon, Icon } from "~/common/components/icon/Icon";
import { useTranslation } from "react-i18next";
import { createContext, type FC, memo, type MouseEventHandler, useContext, useEffect, useState } from "react";
import Parameter from "~/common/components/Parameter";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import LinearATCPopup from "~/features/configurator/common/components/Navigation/LinearATCPopup";
import {
  LinearProductReviewButton,
  LinearProductReviewCard,
} from "~/features/configurator/common/components/Navigation/LinearProductReview";
import { Permission, userHasAnyPermission, userHasPermission } from "~/domains/user/utils/permissions";
import LinearRequestQuotePopup from "~/features/configurator/Linear/components/LinearRequestQuotePopup";
import { userCanPurchaseLinear } from "~/features/configurator/Linear/components/Substeps/useLinearPrice";
import { goToCadSummary } from "~/features/configurator/Linear/redux/actions";
import { SCROLL_TO_LC_SUMMARY_TABS } from "~/features/configurator/common/components/ConfigurationSummarySubstep";
import { useLinearAvailability } from "~/features/configurator/Linear/components/Substeps/useLinearAvailability";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { IconSize } from "~/common/components/icon/IconSize";
import { useConfiguratorPageCustomization } from "./ConfiguratorPageCustomizationContext";

const StyledPageTitle = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  "& > h1": {
    margin: 0,
    fontSize: "inherit",
    lineHeight: "inherit",
  },
});

const StyledHeader = styled(Box)({
  display: "grid",
  gridTemplate: `
    "teaser teaser" auto
    "links summary" auto /
    auto 1fr
  `,
  gridRowGap: "16px",
  marginBottom: "60px",
  "@media (min-width: 1536px)": {
    gridTemplate: `
      "links teaser summary" auto /
      auto 1fr auto
    `,
    gridColumnGap: "32px",
  },
});

// --- LinearLinks Components ---

const LinksCard = styled(Card)({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  borderRadius: "4px",
  padding: "16px",
});

const HelpLink = ({ url }: { url: string }) => {
  const { t } = useTranslation();

  return (
    <Link
      className={"label-large"}
      iconBefore={<Icon type={IconType.QuestionMarkCircle} />}
      href={url}
      target={"_blank"}
    >
      <span>{t("page.linear.links.help")}</span>
    </Link>
  );
};

const ImportConfigurationLink = ({ isPublicUser }: { isPublicUser: boolean }) => {
  const { t } = useTranslation();
  const trackClick = useTrackLCSimpleButtonClick("click_on_open");
  let url;
  if (isPublicUser) {
    getRedirectToRegisterPage({ targetPage: SAVED_CONFIGS_PAGE });
  } else {
    const params = new URLSearchParams();
    params.append("from", window.location.href);
    url = SAVED_CONFIGS_PAGE + "?" + params.toString();
  }
  const onClick = () => {
    trackClick();
  };

  return (
    <Link
      iconBefore={<Icon type={IconType.FolderOpen} />}
      href={isPublicUser ? REGISTER_PAGE : url}
      onClick={!isPublicUser ? onClick : trackClick}
    >
      {t("page.linear.links.import")}
    </Link>
  );
};

const SaveConfigurationLink = ({ isPublicUser }: { isPublicUser: boolean }) => {
  const { t } = useTranslation();
  const trackClick = useTrackLCSimpleButtonClick("click_on_save");
  const disableSave = !useSelector(selectIsSaveConfigurationEnabled);
  const { openModal, component } = useSaveConfigPopup();

  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();
    trackClick();
    if (!isPublicUser) {
      openModal();
    } else {
      redirectOrReload(REGISTER_PAGE);
    }
  };

  return (
    <>
      <Link
        iconBefore={<Icon type={IconType.Disk} />}
        href={isPublicUser ? REGISTER_PAGE : undefined}
        disabled={isPublicUser ? false : disableSave}
        onClick={onClick}
      >
        {t("page.linear.links.saveConfig")}
      </Link>
      {component}
    </>
  );
};

const LinearLinks = ({ className }: { className?: string }) => {
  const isPublicUser = userIsPublic();
  const { t } = useTranslation();
  const { showSave } = useConfiguratorPageCustomization();

  return (
    <LinksCard size={CardSize.SMALL} className={className}>
      <Typography>{t("page.linear.myconfiguration")}</Typography>
      <Stack gap={3} direction={"row"} sx={{ marginTop: "4px" }}>
        <ResetLink />
        {showSave && (
          <>
            <SaveConfigurationLink isPublicUser={isPublicUser} />
            <ImportConfigurationLink isPublicUser={isPublicUser} />
          </>
        )}
      </Stack>
    </LinksCard>
  );
};

// --- LinearSummary Components ---

const StyledSummaryCard = styled(Card)({
  display: "flex",
  position: "relative",
  alignItems: "center",
  borderRadius: "4px",
  padding: "16px",
});

interface ButtonProps {
  className?: string;
  onClick?: MouseEventHandler<HTMLElement>;
  href?: string;
  disabled?: boolean;
}

const webservicesDisabled = window.SCHA.config.disableAllWS;

const AddToCartButton = (props: ButtonProps) => {
  const { t } = useTranslation();
  const canPurchase = userHasPermission(Permission.LINEAR_PURCHASE);
  const label = t(`linearConfigSummary.card.${canPurchase ? "addToCart" : "addToQuote"}`);

  return (
    <Button
      startIcon={<DefaultButtonIcon type={IconType.Cart} />}
      data-test-id={"linear_addToCartButton"}
      disabled={webservicesDisabled || (props.disabled == null ? false : props.disabled)}
      {...props}
      onClick={props.onClick}
    >
      {label}
    </Button>
  );
};

const GoToCartButton = () => {
  const { t } = useTranslation();

  return (
    <Button
      startIcon={<DefaultButtonIcon type={IconType.Cart} />}
      data-test-id={"linear_addToCartButton"}
      href={CART_PAGE}
      disabled={webservicesDisabled}
    >
      {t(`page.linear.addToCart.button.goToCart`)}
    </Button>
  );
};

const LinearAvailability = () => {
  const { t } = useTranslation();
  const { leadTime, isLoadingAvailability, errorLoadingAvailability } = useLinearAvailability();
  const canSeeAvailability = userHasPermission(Permission.AVAILABILITY);

  const isCustomer = userIsCustomer();

  const isLatestKmatUser = userCanPurchaseLinear();

  let leadTimeElement = null;
  if (isCustomer && isLatestKmatUser && canSeeAvailability) {
    if (isLoadingAvailability) {
      leadTimeElement = <CircleSpinner />;
    } else {
      let availabilityText;
      if (errorLoadingAvailability || !leadTime) {
        availabilityText = "-";
      } else {
        availabilityText = t("page.linear.summary.weeks", { count: leadTime });
      }
      leadTimeElement = (
        <Parameter className={"m-0"} label={t("page.linear.summary.availability")} value={availabilityText} />
      );
    }
  }

  return leadTimeElement;
};

const LinearSummary: FC<{ className?: string }> = memo(({ className }) => {
  const disableAddToCart = !useSelector(selectIsConfiguratorAddToCartEnabled);
  const showAddToCart = userHasAnyPermission(Permission.ADD_TO_CART, Permission.ADD_TO_QUOTE);

  const { val: isModalOpen, setTrue: openModal, setFalse: closeModal } = useBooleanState(false);
  const [isRequestQuoteOpen, setIsRequestQuoteOpen] = useState<boolean>(false);
  const openCloseRequestQuote = () => setIsRequestQuoteOpen((prev) => !prev);

  const [reviewOpen, setReviewOpen] = useState(false);

  const isCustomer = userIsCustomer();

  const isLatestKmatUser = userCanPurchaseLinear();

  const { showAvailability, showRequestOffer } = useConfiguratorPageCustomization();

  const isAddedToCartAlready = useSelector(selectIsConfiguratorAddedToCartAlready);

  return (
    <>
      <StyledSummaryCard size={CardSize.SMALL} className={className}>
        <Stack direction={"row"} gap={4}>
          {showAvailability && <LinearAvailability />}

          <CADButton />
          {showRequestOffer && isCustomer && showAddToCart && !isAddedToCartAlready && (
            <AddToCartButton
              disabled={disableAddToCart || webservicesDisabled}
              onClick={isLatestKmatUser ? openModal : openCloseRequestQuote}
            />
          )}
          {showRequestOffer && isCustomer && showAddToCart && isAddedToCartAlready && <GoToCartButton />}
          <LinearProductReviewButton isOpen={reviewOpen} setIsOpen={setReviewOpen} />
        </Stack>
        {reviewOpen && <LinearProductReviewCard isOpen={reviewOpen} setIsOpen={setReviewOpen} />}
      </StyledSummaryCard>
      {isModalOpen && <LinearATCPopup onClose={closeModal} />}
      {isRequestQuoteOpen && <LinearRequestQuotePopup onClose={openCloseRequestQuote} />}
    </>
  );
});

const CADButton = () => {
  const { forceShowCAD } = useConfiguratorPageCustomization();
  const stepsList = useSelector(getSteps);
  const isCustomer = userIsCustomer();
  const isRegistered = userIsRegistered();

  const dispatch = useDispatch();

  const trackCADClick = useTrackLCSimpleButtonClick("click_on_CAD_button");
  const currentStepIndex = useSelector(getCurrentStep);
  const isOnSummaryStep = stepsList.length - 1 === currentStepIndex;

  const redirectToCad = () => {
    if (!forceShowCAD && !isCustomer && !isRegistered) {
      redirectToUrl(
        getRedirectToRegisterPage({ targetPage: document.location.href + "?showCAD=true", openLogin: true }),
      );
    } else {
      trackCADClick();
      dispatch(goToCadSummary());
      if (isOnSummaryStep) {
        SCROLL_TO_LC_SUMMARY_TABS();
      }
    }
  };

  useEffect(() => {
    const logged = window.location.search;
    if (logged === "?showCAD=true") {
      redirectToCad();
    }
  }, []);

  const disableCadButton =
    stepsList && stepsList.length > 0 && stepsList[stepsList.length - 1].active === true ? false : true;

  return (
    <Button
      sx={{ padding: "6px !important" }}
      size={"medium"}
      data-test-id={"linear_cadButton"}
      onClick={redirectToCad}
      disabled={disableCadButton}
    >
      {" "}
      <Icon size={IconSize.FONT_24} type={IconType.Cad} />
    </Button>
  );
};

// --- ConfiguratorPage ---

const StyledLinearLinks = styled(LinearLinks)({
  gridArea: "links",
  justifySelf: "flex-start",
});

const StyledLinearSummary = styled(LinearSummary)({
  gridArea: "summary",
  justifySelf: "flex-end",
});

const StyledLinearTeaser = styled(LinearTeaser)({
  gridArea: "teaser",
  "@media (min-width: 1920px)": {
    justifySelf: "flex-end",
  },
});

export const ConfiguratorPage = ({ config }: { config: ConfiguratorPageDefinition }) => {
  const showLoader = useSelector(getShowLoader);
  const isInConflict = useSelector(getIsInConflict);
  const helpUrl = useSelector(getHelpUrl);

  const isPublicUser = userIsPublic();

  const xmlT = useXmlConfiguratorTranslations().t!;

  // We need to load product data representing this LC configuration
  // ATC needs it later due to getting product descriptor from state
  ProductService.useProductDescriptor({
    productCode: config.productCode,
    priceReference: getInitialPriceReference(),
    options: {
      fetch: true,
      forceFetchBasicData: true,
      fetchAvailability: false,
      fetchPrice: false,
    },
  });

  const title = xmlT(config.translationKeys.pageTitle);

  const customization = useConfiguratorPageCustomization();

  return (
    <Box>
      <PageTitle tag={"div"}>
        <StyledPageTitle>
          <h1>{title}</h1>
          {helpUrl && <HelpLink url={helpUrl} />}
        </StyledPageTitle>
      </PageTitle>
      {customization.showRegistrationBanner && <KbComponent />}
      <StyledHeader>
        <StyledLinearLinks />
        {customization.showRegistrationBanner && isPublicUser && <StyledLinearTeaser />}
        <StyledLinearSummary />
      </StyledHeader>
      <LinearStepNavigation />
      <Step />
      {showLoader && <FullscreenLoader />}
      {isInConflict && <InConflictModal />}
    </Box>
  );
};
